import React, { useState } from "react";
import { Button } from "@component/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Checkbox } from "@component/checkbox";
import { Label } from "@component/label";
import { FileTextIcon, PaperclipIcon, XIcon } from "lucide-react";
import { ContractOffer } from "@type/contract";
import { maxFileSize } from "@constant/app";

interface ContractConfirmationDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (signedContract: File | undefined, successCallback: () => void) => void;
    contract: ContractOffer | null;
}

export function ContractConfirmationDialog({
    isOpen,
    onClose,
    onConfirm,
    contract,
}: ContractConfirmationDialogProps) {
    const [agreed, setAgreed] = useState(false);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [resumeFile, setResumeFile] = useState<File | null>(null);

    if (!isOpen || !contract) return null;

    const handleConfirm = () => {
        setLoading(true);
        if (agreed) {
            onConfirm(resumeFile || undefined, () => {
                setLoading(false);
            });
        }
    };

    const validateFile = (file: File): string | null => {
        const allowedTypes = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
        if (!allowedTypes.includes(file.type)) {
            return "Please upload a PDF, DOC or DOCX file";
        }
        if (file.size > maxFileSize) {
            return "File size must be less than 5MB";
        }
        return null;
    };

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files === null || e.target.files.length === 0) {
            return;
        }

        const file = e.target.files[0];
        const error = validateFile(file);
        if (error) {
            return;
        }

        setResumeFile(file);

        try {
            console.log("upload")
        } catch (error) {
            console.error("Error:", error);
        }
    };

    const handleRemoveFile = (e: React.MouseEvent) => {
        e.stopPropagation();
        setResumeFile(null);
        sessionStorage.removeItem("cv_uuid");
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileTextIcon className="h-5 w-5 text-blue-600" />
                        Confirm Contract Acceptance
                    </DialogTitle>
                    <DialogDescription>
                        Please review and confirm that you agree to all terms and conditions
                        before accepting the contract offer from{" "}
                        <span className="font-medium text-gray-900">
                            {contract?.company_name}
                        </span>{" "}
                        for the{" "}
                        <span className="font-medium text-gray-900">
                            {contract.job_title}
                        </span>{" "}
                        position.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="flex flex-col items-center gap-1 relative cursor-pointer border bg-gray-50 border-dashed border-gray-300 p-5 rounded-sm">
                        {!resumeFile && (
                            <input
                                type="file"
                                id="resume"
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                onChange={handleUpload}
                                accept=".pdf"
                            />
                        )}
                        <PaperclipIcon className="h-5 w-5 text-muted-foreground" />
                        <p className="text-sm font-medium">
                            Upload signed contract
                        </p>

                        {resumeFile && (
                            <>
                                <p className="text-xs text-muted-foreground">
                                    {resumeFile && resumeFile?.name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    {resumeFile ? `${(resumeFile?.size / 1024 / 1024).toFixed(2)} MB` : "PDF up to 5MB"}
                                </p>
                                <div className="flex justify-center space-x-2 mt-2">
                                    <button
                                        onClick={handleRemoveFile}
                                        className="px-4 py-1 border border-gray-300 dark:border-gray-600 rounded-full text-sm flex items-center hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                    >
                                        <XIcon className="h-4 w-4 mr-1" />
                                        Remove
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex items-start space-x-2 pt-2 border-t border-gray-100">
                        <Checkbox
                            id="terms"
                            checked={agreed}
                            onCheckedChange={(checked) => setAgreed(checked as boolean)}
                            className="data-[state=checked]:bg-[#0C49FF] data-[state=checked]:border-[#0C49FF]"
                        />

                        <div className="grid gap-1.5 leading-none">
                            <Label
                                htmlFor="terms"
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                                I have read and agree to all terms and conditions
                            </Label>
                            <p className="text-xs text-gray-500">
                                By checking this box, you confirm that you have read,
                                understood, and agree to all the terms and conditions outlined
                                in the contract.
                            </p>
                        </div>
                    </div>
                </div>

                <DialogFooter className="sm:justify-between">
                    <Button variant="outline" onClick={onClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={!agreed || !resumeFile || isLoading}
                        className="bg-blue-600 hover:bg-blue-800 text-white"
                    >
                        {!isLoading ? "Accept Contract" : (
                            <>
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-1" />
                                Processing...
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
